/**
 * SignaturesSection · v2.7.45+E7 (2026-02-22)
 * ═════════════════════════════════════════════════════════════════════
 * Spec §2 (CRUD list) + §3 (Global Disclaimer surface in same drawer).
 *
 * List of signatures the current user can use, with `+` (create), `-`
 * (delete), and "Duplicate" actions. Editing opens SignatureBuilder.
 */
import { useCallback, useEffect, useState } from "react";
import adminApi from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Edit3, Star, FileSignature, Loader2 } from "lucide-react";
import SignatureBuilder from "./SignatureBuilder";

export default function SignaturesSection({ adminRole }) {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [editingId, setEditing] = useState(null);
  const [busyId, setBusyId]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/email/signatures");
      setItems(data?.items || []);
    } catch (e) {
      toast.error(`Failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      const { data } = await adminApi.post("/admin/email/signatures", {
        name: "New signature",
        layout_json: { template: "classic-left-logo", typography: { accent_color: "#1e3a8a" } },
      });
      await load();
      setEditing(data.id);
    } catch (e) {
      toast.error(`Create failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const duplicate = async (id) => {
    setBusyId(id);
    try {
      await adminApi.post(`/admin/email/signatures/${id}/duplicate`);
      await load();
      toast.success("Duplicated");
    } catch (e) {
      toast.error(`Duplicate failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusyId(null); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete signature "${name}"?`)) return;
    setBusyId(id);
    try {
      await adminApi.delete(`/admin/email/signatures/${id}`);
      await load();
      toast.success("Removed");
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusyId(null); }
  };

  const markDefault = async (id) => {
    setBusyId(id);
    try {
      await adminApi.patch(`/admin/email/signatures/${id}`, { is_default_personal: true });
      await load();
    } catch (e) {
      toast.error(`Set default failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-3" data-testid="signatures-section">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileSignature className="h-4 w-4 text-sky-500"/>
          <div className="text-sm font-extrabold uppercase tracking-widest text-slate-900 dark:text-zinc-100">Signatures</div>
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-700 dark:text-sky-300">{items.length}</span>
        </div>
        <Button
          size="sm"
          className="bg-sky-600 hover:bg-sky-500 text-white font-bold h-7 px-2 text-[11px] gap-1"
          onClick={create}
          data-testid="signature-create-btn"
        >
          <Plus className="h-3 w-3"/>New signature
        </Button>
      </header>

      <div className="space-y-2">
        {loading && items.length === 0 && (
          <div className="text-center py-6 text-slate-500 dark:text-zinc-500 text-xs flex items-center justify-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin"/>Loading…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="text-center py-6 border border-dashed border-slate-300 dark:border-zinc-700 rounded text-xs text-slate-500 dark:text-zinc-500">
            No signatures yet — click <b>New signature</b> to build your first.
          </div>
        )}
        {items.map((s) => (
          <div
            key={s.id}
            className="border border-slate-200 dark:border-zinc-800 rounded p-3 bg-slate-50/50 dark:bg-zinc-900/40 flex items-start gap-3"
            data-testid={`signature-row-${s.id}`}
          >
            {/* Compact preview chip */}
            <div className="hidden sm:block w-32 h-16 bg-white rounded border border-slate-200 dark:border-zinc-800 overflow-hidden flex-shrink-0 relative">
              <div
                className="absolute inset-0 origin-top-left scale-[0.20] w-[500%] h-[500%] pointer-events-none"
                dangerouslySetInnerHTML={{ __html: s.html || "" }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-sm font-bold text-slate-900 dark:text-zinc-100 truncate">{s.name}</div>
                {s.is_system && (
                  <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300">system</span>
                )}
                {!s.is_system && s.shared && (
                  <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">team</span>
                )}
                {s.is_default_personal && (
                  <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">default</span>
                )}
              </div>
              <div className="text-[11px] text-slate-500 dark:text-zinc-500 mt-0.5">
                {s.layout_json?.name && <>{s.layout_json.name} · </>}
                Updated {s.updated_at && new Date(s.updated_at).toLocaleDateString()}
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Button
                size="sm" variant="outline" className="h-7 px-2 text-[10px] gap-1"
                onClick={() => setEditing(s.id)}
                data-testid={`signature-edit-${s.id}`}
              >
                <Edit3 className="h-3 w-3"/>Edit
              </Button>
              <Button
                size="sm" variant="ghost" className="h-7 px-2 text-[10px] gap-1"
                onClick={() => duplicate(s.id)}
                disabled={busyId === s.id}
                data-testid={`signature-dup-${s.id}`}
                title="Duplicate"
              >
                <Copy className="h-3 w-3"/>
              </Button>
              {!s.is_default_personal && (
                <Button
                  size="sm" variant="ghost" className="h-7 px-2 text-[10px] gap-1"
                  onClick={() => markDefault(s.id)}
                  disabled={busyId === s.id}
                  data-testid={`signature-default-${s.id}`}
                  title="Set as my default"
                >
                  <Star className="h-3 w-3"/>
                </Button>
              )}
              {!s.is_system && (
                <Button
                  size="sm" variant="ghost"
                  className="h-7 w-7 p-0 text-rose-600 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                  onClick={() => remove(s.id, s.name)}
                  disabled={busyId === s.id}
                  data-testid={`signature-delete-${s.id}`}
                  title="Delete"
                >
                  <Trash2 className="h-3 w-3"/>
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {editingId && (
        <SignatureBuilder
          signatureId={editingId}
          onClose={() => { setEditing(null); load(); }}
          onSaved={() => load()}
        />
      )}
    </div>
  );
}
